const reglossCartDrawerSectionsUrl = (cartJson) => {
  if (!cartJson?.items?.length) return window.location.pathname;

  for (const item of cartJson.items) {
    if (item.url) {
      try {
        const path = new URL(item.url, window.location.origin).pathname;
        if (path.includes("/products/")) return path;
      } catch {
        /* ignore */
      }
    }
    if (item.handle) return `/products/${item.handle}`;
  }

  return window.location.pathname;
};

window.reglossCartDrawerSectionsUrl = reglossCartDrawerSectionsUrl;

class ReglossCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.addEventListener("click", this.onDrawerClick.bind(this), true);
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (!this.dataset.shopifySectionId) {
      const wrapper = this.closest(".shopify-section");
      if (wrapper?.id?.startsWith("shopify-section-")) {
        this.dataset.shopifySectionId = wrapper.id.replace(
          "shopify-section-",
          "",
        );
      }
    }

    this.moveToBody();

    if (window.reglossPendingCartDrawerOpen) {
      window.reglossPendingCartDrawerOpen = false;
      this.refreshFromServer().catch((error) => {
        console.error(error);
        this.open();
      });
    }
  }

  moveToBody() {
    if (this.parentElement === document.body) return;
    document.body.appendChild(this);
  }

  onDrawerClick(event) {
    if (event.target.closest("[data-regloss-cart-close]")) {
      this.close();
      return;
    }

    const checkoutButton = event.target.closest(
      ".regloss-cart-drawer__checkout",
    );
    if (checkoutButton && !checkoutButton.disabled) {
      event.preventDefault();
      window.reglossDismissCartDrawerAutoOpen?.();
      window.location.assign("/checkout");
      return;
    }

    const upsellButton = event.target.closest("[data-regloss-upsell-add]");
    if (upsellButton) {
      this.onUpsellAdd(upsellButton);
    }
  }

  isExperienceActive() {
    return document.documentElement.classList.contains("ab-cart-drawer");
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.addEventListener("click", (event) => {
      if (!this.isExperienceActive()) return;
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener("keydown", (event) => {
      if (!this.isExperienceActive()) return;
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (!this.isExperienceActive()) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn =
          document.getElementById("ReglossCartDrawer");
        const focusElement =
          this.querySelector(".regloss-cart-drawer__close") ||
          this.querySelector(".regloss-cart-drawer__checkout");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.remove("regloss-cart-drawer-pending");
    document.body.classList.add("overflow-hidden", "regloss-cart-drawer-open");
    window.reglossApplyBogosGiftPopupSuppression?.();
    this.dispatchEvent(
      new CustomEvent("regloss-cart-drawer:opened", { bubbles: true }),
    );
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove(
      "overflow-hidden",
      "regloss-cart-drawer-open",
      "regloss-cart-drawer-pending",
    );
    this.dispatchEvent(
      new CustomEvent("regloss-cart-drawer:closed", { bubbles: true }),
    );
  }

  renderContents(parsedState) {
    if (!this.isExperienceActive()) return;

    this.moveToBody();

    const panel = this.querySelector(".regloss-cart-drawer__panel");
    if (panel?.classList.contains("regloss-cart-drawer__panel--empty")) {
      panel.classList.remove("regloss-cart-drawer__panel--empty");
    }

    this.classList.toggle("is-empty", parsedState.item_count === 0);

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const innerHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (innerHtml) sectionElement.innerHTML = innerHtml;
    });

    this.syncDrawerConfigFromParsedState(parsedState);
    setTimeout(() => this.open());
  }

  syncDrawerConfigFromParsedState(parsedState) {
    const sectionHtml = parsedState.sections?.[this.getDrawerSectionId()];
    if (!sectionHtml) return;

    const drawerDoc = new DOMParser().parseFromString(sectionHtml, "text/html");
    const sourceConfig = drawerDoc.getElementById("ReglossCartDrawerConfig");
    const targetConfig = this.querySelector("#ReglossCartDrawerConfig");
    if (sourceConfig && targetConfig) {
      targetConfig.textContent = sourceConfig.textContent;
    }
    window.reglossSyncBundleLinePropertiesFromConfig?.();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const parsedSection = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return parsedSection?.innerHTML || "";
  }

  getDrawerSectionId() {
    return (
      this.dataset.shopifySectionId ||
      this.dataset.sectionId ||
      this.getAttribute("data-section-id") ||
      "regloss-cart-drawer"
    );
  }

  getSectionsToRender() {
    return [
      {
        id: this.getDrawerSectionId(),
        selector: "#ReglossCartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async refreshFromServer({ open = true } = {}) {
    if (!this.isExperienceActive()) return;

    this.moveToBody();

    const cartUrl = window.routes?.cart_url || "/cart";
    const drawerSectionId = this.getDrawerSectionId();

    try {
      const cartJson = await fetch(`${cartUrl}.js`).then((response) =>
        response.json(),
      );

      const sectionsUrl = reglossCartDrawerSectionsUrl(cartJson);

      const drawerSectionQuery = new URLSearchParams({
        section_id: drawerSectionId,
        sections_url: sectionsUrl,
      });

      const [drawerHtml, bubbleHtml] = await Promise.all([
        fetch(`${cartUrl}?${drawerSectionQuery.toString()}`).then((response) =>
          response.text(),
        ),
        fetch(`${cartUrl}?section_id=cart-icon-bubble`).then((response) =>
          response.text(),
        ),
      ]);

      const drawerDoc = new DOMParser().parseFromString(
        drawerHtml,
        "text/html",
      );
      const sourcePanel = drawerDoc.querySelector(
        ".regloss-cart-drawer__panel",
      );
      const targetPanel = this.querySelector(".regloss-cart-drawer__panel");

      const sourceConfig = drawerDoc.getElementById("ReglossCartDrawerConfig");
      const targetConfig = this.querySelector("#ReglossCartDrawerConfig");
      if (sourceConfig && targetConfig) {
        targetConfig.textContent = sourceConfig.textContent;
      }
      window.reglossSyncBundleLinePropertiesFromConfig?.();

      if (sourcePanel && targetPanel) {
        targetPanel.innerHTML = sourcePanel.innerHTML;
      } else {
        const sourceDrawer = drawerDoc.querySelector("#ReglossCartDrawer");
        const targetDrawer = document.getElementById("ReglossCartDrawer");
        if (sourceDrawer && targetDrawer) {
          targetDrawer.innerHTML = sourceDrawer.innerHTML;
        }
      }

      const bubbleDoc = new DOMParser().parseFromString(
        bubbleHtml,
        "text/html",
      );
      const sourceBubble = bubbleDoc.querySelector(".shopify-section");
      const targetBubble = document.getElementById("cart-icon-bubble");

      if (sourceBubble && targetBubble) {
        targetBubble.innerHTML = sourceBubble.innerHTML;
      }

      this.classList.toggle("is-empty", cartJson.item_count === 0);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "regloss-cart-drawer",
        cartData: cartJson,
      });

      if (open) this.open();
    } catch (error) {
      console.error(error);
      if (open) this.open();
    }
  }

  async onUpsellAdd(button) {
    if (button.disabled || button.classList.contains("loading")) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.classList.add("loading");
    button.setAttribute("aria-disabled", "true");

    let lineProperties = {};
    if (
      typeof window.reglossBuildFuelerateBundleLineProperties === "function"
    ) {
      lineProperties = await window.reglossBuildFuelerateBundleLineProperties();
    }

    const body = {
      items: [
        {
          id: variantId,
          quantity: 1,
          properties:
            typeof window.reglossMergeFuelerateBundleLineProperties ===
            "function"
              ? window.reglossMergeFuelerateBundleLineProperties(
                  lineProperties,
                  { asBundleRec: true },
                )
              : {
                  ...lineProperties,
                  _recommend_fuelerate_bundle: "true",
                },
        },
      ],
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url:
        this.querySelector("regloss-cart-drawer-items")?.getSectionsUrl?.() ||
        reglossCartDrawerSectionsUrl({
          items: [{ url: window.location.pathname }],
        }),
    };

    fetch(`${routes.cart_add_url}`, {
      ...fetchConfig(),
      ...{ body: JSON.stringify(body) },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error(response.description || response.message);
          return;
        }

        window.reglossUpdateBundleLinePropertiesCacheFromItems?.(
          response.items || [],
        );

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "regloss-cart-drawer",
          cartData: response,
          productVariantId: variantId,
        });

        this.renderContents(response);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        button.classList.remove("loading");
        button.removeAttribute("aria-disabled");
      });
  }
}

customElements.define("regloss-cart-drawer", ReglossCartDrawer);

class ReglossCartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    this.reglossRefreshSectionsUrl();
    this.reglossCartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      () => {
        this.reglossRefreshSectionsUrl();
      },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.reglossCartUpdateUnsubscriber?.();
  }

  reglossRefreshSectionsUrl() {
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cartJson) => {
        this._reglossSectionsUrl = reglossCartDrawerSectionsUrl(cartJson);
      })
      .catch(() => {});
  }

  getSectionsUrl() {
    return this._reglossSectionsUrl || window.location.pathname;
  }

  getSectionsToRender() {
    const drawer = this.closest("regloss-cart-drawer");
    const drawerSectionId =
      drawer?.dataset?.shopifySectionId ||
      drawer?.dataset?.sectionId ||
      "regloss-cart-drawer";

    return [
      {
        id: "ReglossCartDrawer",
        section: drawerSectionId,
        selector: ".regloss-cart-drawer__panel",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("regloss-cart-drawer-items", ReglossCartDrawerItems);
